/**
 * How a tenant is NAMED on screen — one implementation for the whole super-admin plane.
 *
 * A tenant document is not guaranteed to carry `name` or `owner_email`: several signup
 * paths create the row before those fields exist, and a tenant created directly (support,
 * migration, fixture) may never get them. Rendering `{tenant.name}` then produces an empty
 * `<h1>`, a blank PRACTICE column, and — where a separator was hardcoded around the field
 * — a dangling "· ·" with nothing between the dots. An operator scanning a long list
 * cannot tell a malformed tenant from a loading placeholder, which is exactly the row they
 * should be most careful with before suspending or deleting it.
 *
 * The backend already learned this: `super_admin_tenant360_routes` carries the SAME
 * fallback at six cross-tenant enrichment sites, each with the same comment. The frontend
 * counterparts were missed. This file exists so the seventh, eighth and ninth call sites
 * inherit the answer instead of repeating the omission — if you find yourself typing
 * `tenant.name ||` anywhere under `super_admin/`, import from here instead.
 *
 * The fallback chain is ordered by how much it tells a human: the practice's own name,
 * then the address they chose, then the immutable id. The last is always present (it is
 * the primary key), so the chain cannot end empty.
 */

/** Display name of a tenant. Never empty when the row has an identity at all. */
export function tenantDisplayName(tenant) {
  const t = tenant || {};
  return t.name || t.slug || t.subdomain || t.tenant_id || "";
}

/**
 * Owner email, or `null` when there is none.
 *
 * Returning `null` rather than `""` is deliberate: the caller has to branch, and a
 * branch is what makes the surrounding separator disappear with the value. `""` renders
 * as nothing and leaves the "·" behind — the exact artefact this replaces.
 */
export function tenantOwnerEmail(tenant) {
  const email = (tenant || {}).owner_email;
  return email ? email : null;
}

/**
 * The parts of a tenant's subtitle line that actually exist, ready to be joined by a
 * separator. Callers render `parts.join(" · ")` — or interleave elements — instead of
 * hardcoding a separator around a field that may be absent.
 */
export function tenantSubtitleParts(tenant) {
  const t = tenant || {};
  return [t.slug ? `/${t.slug}` : null, tenantOwnerEmail(t)].filter(Boolean);
}

/**
 * The address of a practice's public website, for display under its name: host (and port,
 * if any) plus the path when there is one, with no scheme, query or trailing slash.
 *
 * `website` is the server-resolved advertised address: an origin
 * (`https://emilie.solunary.health` -> `emilie.solunary.health`) for every practice but the
 * root one, which the platform host serves under a path (`https://solunary.health/demo` ->
 * `solunary.health/demo`, the address its "View site" opens on Tenants). Only `http:` and
 * `https:` URLs yield an address: an empty, malformed or other-scheme value (`javascript:`,
 * `ftp:`) yields `""`, so a caller omits the part instead of printing an internal id or an
 * unsafe string in its place.
 */
export function siteAddress(website) {
  if (typeof website !== "string" || !website) return "";
  let url;
  try {
    url = new URL(website);
  } catch {
    return "";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return "";
  return `${url.host}${url.pathname.replace(/\/+$/, "")}`;
}
